import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

function AzureIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 -28.5 256 256" xmlns="http://www.w3.org/2000/svg">
      <path d="M118.431947,187.698037 C151.322003,181.887937 178.48731,177.08008 178.799309,177.013916 L179.366585,176.893612 L148.31513,139.958881 C131.236843,119.644776 117.26369,102.945381 117.26369,102.849118 C117.26369,102.666861 149.32694,14.3716012 149.507189,14.057257 C149.567455,13.952452 171.38747,51.62411 202.400338,105.376064 C231.435152,155.699606 255.372949,197.191547 255.595444,197.580359 L255.999996,198.287301 L157.315912,198.274572 L58.6318456,198.261895 L118.431947,187.698073 L118.431947,187.698037 Z M-4.03864498e-06,176.434723 C-4.03864498e-06,176.382721 14.631291,150.983941 32.5139844,119.992969 L65.0279676,63.6457518 L102.919257,31.8473052 C123.759465,14.3581634 140.866667,0.0274832751 140.935253,0.00062917799 C141.003839,-0.0247829554 140.729691,0.665213042 140.326034,1.53468179 C139.922377,2.40415053 121.407304,42.1170321 99.1814268,89.7855264 L58.7707514,176.455514 L29.3853737,176.492355 C13.2234196,176.512639 -4.03864498e-06,176.486664 -4.03864498e-06,176.434703 L-4.03864498e-06,176.434723 Z" fill="#0089D6" fillRule="nonzero" />
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 -0.5 25 25" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
      <path d="m12.301 0h.093c2.242 0 4.34.613 6.137 1.68l-.055-.031c1.871 1.094 3.386 2.609 4.449 4.422l.031.058c1.04 1.769 1.654 3.896 1.654 6.166 0 5.406-3.483 10-8.327 11.658l-.087.026c-.063.02-.135.031-.209.031-.162 0-.312-.054-.433-.144l.002.001c-.128-.115-.208-.281-.208-.466 0-.005 0-.01 0-.014v.001q0-.048.008-1.226t.008-2.154c.007-.075.011-.161.011-.249 0-.792-.323-1.508-.844-2.025.618-.061 1.176-.163 1.718-.305l-.076.017c.573-.16 1.073-.373 1.537-.642l-.031.017c.508-.28.938-.636 1.292-1.058l.006-.007c.372-.476.663-1.036.84-1.645l.009-.035c.209-.683.329-1.468.329-2.281 0-.045 0-.091-.001-.136v.007c0-.022.001-.047.001-.072 0-1.248-.482-2.383-1.269-3.23l.003.003c.168-.44.265-.948.265-1.479 0-.649-.145-1.263-.404-1.814l.011.026c-.115-.022-.246-.035-.381-.035-.334 0-.649.078-.929.216l.012-.005c-.568.21-1.054.448-1.512.726l.038-.022-.609.384c-.922-.264-1.981-.416-3.075-.416s-2.153.152-3.157.436l.081-.02q-.256-.176-.681-.433c-.373-.214-.814-.421-1.272-.595l-.066-.022c-.293-.154-.64-.244-1.009-.244-.124 0-.246.01-.364.03l.013-.002c-.248.524-.393 1.139-.393 1.788 0 .531.097 1.04.275 1.509l-.01-.029c-.785.844-1.266 1.979-1.266 3.227 0 .025 0 .051.001.076v-.004c-.001.039-.001.084-.001.13 0 .809.12 1.591.344 2.327l-.015-.057c.189.643.476 1.202.85 1.693l-.009-.013c.354.435.782.793 1.267 1.062l.022.011c.432.252.933.465 1.46.614l.046.011c.466.125 1.024.227 1.595.284l.046.004c-.431.428-.718 1-.784 1.638l-.001.012c-.207.101-.448.183-.699.236l-.021.004c-.256.051-.549.08-.85.08-.022 0-.044 0-.066 0h.003c-.394-.008-.756-.136-1.055-.348l.006.004c-.371-.259-.671-.595-.881-.986l-.007-.015c-.198-.336-.459-.614-.768-.827l-.009-.006c-.225-.169-.49-.301-.776-.38l-.016-.004-.32-.048c-.023-.002-.05-.003-.077-.003-.14 0-.273.028-.394.077l.007-.003q-.128.072-.08.184c.039.086.087.16.145.225l-.001-.001c.061.072.13.135.205.19l.003.002.112.08c.283.148.516.354.693.603l.004.006c.191.237.359.505.494.792l.01.024.16.368c.135.402.38.738.7.981l.005.004c.3.234.662.402 1.057.478l.016.002c.33.064.714.104 1.106.112h.007c.045.002.097.002.15.002.261 0 .517-.021.767-.062l-.027.004.368-.064q0 .609.008 1.418t.008.873v.014c0 .185-.08.351-.208.466h-.001c-.119.089-.268.143-.431.143-.075 0-.147-.011-.214-.032l.005.001c-4.929-1.689-8.409-6.283-8.409-11.69 0-2.268.612-4.393 1.681-6.219l-.032.058c1.094-1.871 2.609-3.386 4.422-4.449l.058-.031c1.739-1.034 3.835-1.645 6.073-1.645h.098-.005zm-7.64 17.666q.048-.112-.112-.192-.16-.048-.208.032-.048.112.112.192.144.096.208-.032zm.497.545q.112-.08-.032-.256-.16-.144-.256-.048-.112.08.032.256.159.157.256.047zm.48.72q.144-.112 0-.304-.128-.208-.272-.096-.144.08 0 .288t.272.112zm.672.673q.128-.128-.064-.304-.192-.192-.32-.048-.144.128.064.304.192.192.32.044zm.913.4q.048-.176-.208-.256-.24-.064-.304.112t.208.24q.24.097.304-.096zm1.009.08q0-.208-.272-.176-.256 0-.256.176 0 .208.272.176.256.001.256-.175zm.929-.16q-.032-.176-.288-.144-.256.048-.224.24t.288.128.225-.224z" />
    </svg>
  );
}

function RailwayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
      <path d="M77.354 52.4286C78.6655 48.525 79.3762 44.3455 79.3762 40C79.3762 18.4609 61.9153 1 40.3762 1C18.8371 1 1.37619 18.4609 1.37619 40C1.37619 44.3455 2.08689 48.525 3.3984 52.4286H77.354Z" fill="currentColor" />
      <path d="M75.8056 56.3247H4.94684C11.1248 69.7105 24.6657 79 40.3762 79C56.0867 79 69.6276 69.7105 75.8056 56.3247Z" fill="currentColor" />
    </svg>
  );
}

function CloudflareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 2 75 36" xmlns="http://www.w3.org/2000/svg" fill="none">
      <path d="M51.027 36.4346L51.405 35.1054C51.8639 33.532 51.6929 32.0671 50.928 31.0001C50.2261 30.0144 49.0562 29.4358 47.6343 29.3635L20.7268 29.0199C20.5469 29.0108 20.3939 28.9294 20.3039 28.7937C20.2139 28.6581 20.1869 28.4772 20.25 28.3054C20.3399 28.0432 20.6009 27.8353 20.8709 27.8262L48.0212 27.4826C51.243 27.3379 54.7257 24.7066 55.9496 21.5055L57.4974 17.4364C57.5424 17.3279 57.5604 17.2104 57.5604 17.0928C57.5604 17.0295 57.5514 16.9662 57.5424 16.9029C55.7966 8.95458 48.7322 3.01367 40.3 3.01367C32.5248 3.01367 25.9194 8.05937 23.5526 15.0673C22.0227 13.9189 20.0699 13.304 17.9641 13.5119C14.2295 13.8827 11.2328 16.9029 10.8638 20.6555C10.7648 21.6321 10.8458 22.5634 11.0707 23.4496C4.97834 23.6305 0.0917969 28.649 0.0917969 34.807C0.0917969 35.3676 0.136793 35.9101 0.208786 36.4527C0.244782 36.715 0.469762 36.9048 0.730737 36.9048L50.397 36.9139C50.4061 36.9139 50.4061 36.9139 50.4151 36.9139C50.6941 36.9048 50.946 36.715 51.027 36.4346Z" fill="#F6821F"/>
      <path d="M59.9905 17.7363C59.7386 17.7363 59.4955 17.7454 59.2436 17.7544C59.1986 17.7544 59.1626 17.7634 59.1266 17.7815C59.0006 17.8267 58.8926 17.9352 58.8567 18.0709L57.7948 21.7421C57.3357 23.3155 57.5068 24.7803 58.2717 25.8473C58.9736 26.833 60.1435 27.4117 61.5654 27.4841L67.2979 27.8277C67.4688 27.8367 67.6129 27.9181 67.7028 28.0537C67.8018 28.1894 67.8198 28.3702 67.7658 28.5421C67.6758 28.8042 67.4148 29.0122 67.1449 29.0213L61.1874 29.3649C57.9567 29.5186 54.465 32.1409 53.2411 35.342L52.8092 36.4722C52.7282 36.6803 52.8812 36.8973 53.0881 36.9063C53.0972 36.9063 53.0972 36.9063 53.1062 36.9063H73.6153C73.8583 36.9063 74.0742 36.7435 74.1463 36.5085C74.5062 35.2334 74.6952 33.8952 74.6952 32.5027C74.6952 24.3554 68.1078 17.7363 59.9905 17.7363Z" fill="#FBAD41"/>
    </svg>
  );
}

function ProviderCard({ name, description, icon }: { name: string; description: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-5 hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-7 w-7 shrink-0">{icon}</div>
        <h4 className="font-semibold text-gray-900 dark:text-white">{name}</h4>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'supported-providers', label: 'Supported Providers' },
  { id: 'azure-devops', label: 'Azure DevOps' },
  { id: 'azure-config', label: 'Configuration', level: 1 },
  { id: 'azure-data-sources', label: 'Data Sources', level: 1 },
  { id: 'github-actions', label: 'GitHub Actions' },
  { id: 'github-config', label: 'Configuration', level: 1 },
  { id: 'github-data-sources', label: 'Data Sources', level: 1 },
  { id: 'railway', label: 'Railway' },
  { id: 'railway-config', label: 'Configuration', level: 1 },
  { id: 'railway-data-sources', label: 'Data Sources', level: 1 },
  { id: 'cloudflare', label: 'Cloudflare' },
  { id: 'cloudflare-data-sources', label: 'Data Sources', level: 1 },
  { id: 'cloudflare-data-models', label: 'Data Models', level: 1 },
  { id: 'cloudflare-requirements', label: 'Requirements', level: 1 },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'polling', label: 'Polling & Sync' },
  { id: 'data-models', label: 'Data Models' },
  { id: 'validation', label: 'Connection Validation' },
  { id: 'managing', label: 'Managing Integrations' },
  { id: 'coming-soon', label: 'Coming Soon' },
];

export function CloudSetupPage() {
  useDocumentMeta({ title: 'Cloud Setup: LogPulse Docs', description: 'Connect LogPulse to Azure DevOps, GitHub Actions, Railway, and Cloudflare for automated log ingestion.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.integrations')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.cloud.title')}
          </h1>
          <P>{t('docs.cloud.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.cloud.overview.title')}>
          <P>{t('docs.cloud.overview.description')}</P>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">4</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.cloud.overview.providersCount')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400 mb-1">2</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.cloud.overview.collectionMethods')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-info-600 dark:text-info-400 mb-1">5+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.cloud.overview.dataSourcesCount')}</div>
            </div>
          </div>
          <Callout type="info">{t('docs.cloud.overview.note')}</Callout>
        </Section>

        {/* Supported Providers */}
        <Section id="supported-providers" title={t('docs.cloud.providers.title')}>
          <P>{t('docs.cloud.providers.description')}</P>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
            <ProviderCard
              name="Azure DevOps"
              description={t('docs.cloud.providers.azureDesc')}
              icon={<AzureIcon className="h-7 w-7" />}
            />
            <ProviderCard
              name="GitHub Actions"
              description={t('docs.cloud.providers.githubDesc')}
              icon={<GitHubIcon className="h-7 w-7 text-gray-900 dark:text-white" />}
            />
            <ProviderCard
              name="Railway"
              description={t('docs.cloud.providers.railwayDesc')}
              icon={<RailwayIcon className="h-7 w-7 text-gray-900 dark:text-white" />}
            />
            <ProviderCard
              name="Cloudflare"
              description={t('docs.cloud.providers.cloudflareDesc')}
              icon={<CloudflareIcon className="h-7 w-7" />}
            />
          </div>
        </Section>

        {/* Azure DevOps */}
        <Section id="azure-devops" title={t('docs.cloud.azure.title')}>
          <P>{t('docs.cloud.azure.description')}</P>

          <SubSection id="azure-config" title={t('docs.cloud.azure.config.title')}>
            <P>{t('docs.cloud.azure.config.description')}</P>
            <Table
              headers={['Field', 'Required', 'Description']}
              rows={[
                ['Organization URL', 'Yes', t('docs.cloud.azure.config.orgUrl')],
                ['Personal Access Token', 'Yes', t('docs.cloud.azure.config.pat')],
                ['Project Name', 'No', t('docs.cloud.azure.config.project')],
              ]}
            />
            <Callout type="tip">{t('docs.cloud.azure.config.patTip')}</Callout>
            <P>{t('docs.cloud.azure.config.patScopes')}</P>
            <CodeBlock title="Required PAT Scopes">{`• Audit Log: Read
• Build: Read
• Code: Read
• Project and Team: Read
• Service Connections: Read`}</CodeBlock>
          </SubSection>

          <SubSection id="azure-data-sources" title={t('docs.cloud.azure.dataSources.title')}>
            <P>{t('docs.cloud.azure.dataSources.description')}</P>
            <Table
              headers={['Data Source', 'Method', 'Events']}
              rows={[
                ['Audit Logs', 'Poll', t('docs.cloud.azure.dataSources.auditEvents')],
                ['Service Hooks', 'Webhook', t('docs.cloud.azure.dataSources.hookEvents')],
                ['Pipeline Logs', 'Poll', t('docs.cloud.azure.dataSources.pipelineEvents')],
              ]}
            />
          </SubSection>
        </Section>

        {/* GitHub Actions */}
        <Section id="github-actions" title={t('docs.cloud.github.title')}>
          <P>{t('docs.cloud.github.description')}</P>

          <SubSection id="github-config" title={t('docs.cloud.github.config.title')}>
            <P>{t('docs.cloud.github.config.description')}</P>
            <Table
              headers={['Field', 'Required', 'Description']}
              rows={[
                ['Personal Access Token', 'Yes', t('docs.cloud.github.config.pat')],
                ['Owner', 'Yes', t('docs.cloud.github.config.owner')],
                ['Repository', 'No', t('docs.cloud.github.config.repo')],
              ]}
            />
            <Callout type="tip">{t('docs.cloud.github.config.patTip')}</Callout>
            <P>{t('docs.cloud.github.config.patScopes')}</P>
            <CodeBlock title="Required Token Permissions">{`• actions: read
• contents: read
• metadata: read`}</CodeBlock>
          </SubSection>

          <SubSection id="github-data-sources" title={t('docs.cloud.github.dataSources.title')}>
            <P>{t('docs.cloud.github.dataSources.description')}</P>
            <Table
              headers={['Data Source', 'Method', 'Events']}
              rows={[
                ['Workflow Runs', 'Poll', t('docs.cloud.github.dataSources.workflowRunEvents')],
                ['Workflow Jobs', 'Poll', t('docs.cloud.github.dataSources.workflowJobEvents')],
                ['Repository Events', 'Webhook', t('docs.cloud.github.dataSources.repoEvents')],
              ]}
            />
          </SubSection>
        </Section>

        {/* Railway */}
        <Section id="railway" title={t('docs.cloud.railway.title')}>
          <P>{t('docs.cloud.railway.description')}</P>

          <SubSection id="railway-config" title={t('docs.cloud.railway.config.title')}>
            <P>{t('docs.cloud.railway.config.description')}</P>
            <Table
              headers={['Field', 'Required', 'Description']}
              rows={[
                ['API Token', 'Yes', t('docs.cloud.railway.config.token')],
                ['Project ID', 'Yes', t('docs.cloud.railway.config.projectId')],
                ['Environment ID', 'No', t('docs.cloud.railway.config.envId')],
                ['Service ID', 'No', t('docs.cloud.railway.config.serviceId')],
              ]}
            />
            <Callout type="info">{t('docs.cloud.railway.config.note')}</Callout>
          </SubSection>

          <SubSection id="railway-data-sources" title={t('docs.cloud.railway.dataSources.title')}>
            <P>{t('docs.cloud.railway.dataSources.description')}</P>
            <Table
              headers={['Data Source', 'Method', 'Events']}
              rows={[
                ['Deployments', 'Poll', t('docs.cloud.railway.dataSources.deployEvents')],
                ['Build Logs', 'Poll', t('docs.cloud.railway.dataSources.buildEvents')],
                ['Runtime Logs', 'Poll', t('docs.cloud.railway.dataSources.runtimeEvents')],
                ['Deployment Events', 'Webhook', t('docs.cloud.railway.dataSources.webhookEvents')],
              ]}
            />
          </SubSection>
        </Section>

        {/* Cloudflare */}
        <Section id="cloudflare" title={t('docs.cloud.cloudflare.title')}>
          <P>{t('docs.cloud.cloudflare.description')}</P>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-6">
            {[
              'Edge HTTP request logs with RayID tracing',
              'WAF/Firewall security event monitoring',
              'Account audit trail for compliance (NIS2)',
              'Workers trace events (poll + OTLP push)',
              'Pages deployment tracking',
              'Zero Trust Access & Gateway logs',
              'DNS query analytics',
              'Origin healthcheck monitoring',
            ].map((item) => (
              <div key={item} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-success-500 shrink-0" />
                {item}
              </div>
            ))}
          </div>
          <Callout type="info">{t('docs.cloud.cloudflare.config.otlpNote')}</Callout>

          <SubSection id="cloudflare-data-sources" title={t('docs.cloud.cloudflare.dataSources.title')}>
            <P>{t('docs.cloud.cloudflare.dataSources.description')}</P>
            <Table
              headers={['Data Source', 'Plan', 'Method', 'Description']}
              rows={[
                ['HTTP Request Logs', 'Enterprise', 'Poll', t('docs.cloud.cloudflare.dataSources.httpEvents')],
                ['DNS Query Logs', 'Enterprise', 'Poll', t('docs.cloud.cloudflare.dataSources.dnsEvents')],
                ['Firewall / WAF Events', 'Free', 'Poll', t('docs.cloud.cloudflare.dataSources.wafEvents')],
                ['Account Audit Logs', 'Free', 'Poll', t('docs.cloud.cloudflare.dataSources.auditEvents')],
                ['Zero Trust Access Logs', 'Zero Trust', 'Poll', t('docs.cloud.cloudflare.dataSources.ztAccessEvents')],
                ['Zero Trust Gateway Logs', 'Zero Trust', 'Poll', t('docs.cloud.cloudflare.dataSources.ztGatewayEvents')],
                ['Workers & Pages Logs', 'Workers Paid', 'Push (OTLP)', t('docs.cloud.cloudflare.dataSources.workersLogEvents')],
                ['Workers & Pages Traces', 'Workers Paid', 'Push (OTLP)', t('docs.cloud.cloudflare.dataSources.workersTraceEvents')],
                ['Pages Deployments', 'Free', 'Poll', t('docs.cloud.cloudflare.dataSources.pagesDeployEvents')],
                ['Healthcheck Events', 'Pro', 'Poll', t('docs.cloud.cloudflare.dataSources.healthcheckEvents')],
              ]}
            />
          </SubSection>

          <SubSection id="cloudflare-data-models" title={t('docs.cloud.cloudflare.dataModels.title')}>
            <P>{t('docs.cloud.cloudflare.dataModels.description')}</P>
            <Table
              headers={['Model', 'Sourcetype', 'Sources', 'Description']}
              rows={[
                ['Application Log', 'cloudflare:workers:log', 'Workers & Pages Logs', t('docs.cloud.cloudflare.dataModels.appLogDesc')],
                ['Security Event', 'security_event', 'Firewall, Audit, Access, Gateway', t('docs.cloud.cloudflare.dataModels.securityDesc')],
                ['Trace Event', 'observability_trace', 'Workers & Pages Traces', t('docs.cloud.cloudflare.dataModels.traceDesc')],
                ['CI/CD Pipeline', 'cicd_pipeline', 'Pages Deployments', t('docs.cloud.cloudflare.dataModels.cicdDesc')],
                ['Observability Alert', 'observability_alert', 'Healthchecks', t('docs.cloud.cloudflare.dataModels.alertDesc')],
              ]}
            />
            <CodeBlock title="LPQL Example: Query Workers logs">{`sourcetype="cloudflare:workers:log" level="error" | stats count by service_name`}</CodeBlock>
            <CodeBlock title="LPQL Example: Query WAF blocks">{`sourcetype="security_event" waf_action="block" | stats count by path, rule_id`}</CodeBlock>
          </SubSection>

          <SubSection id="cloudflare-requirements" title={t('docs.cloud.cloudflare.requirements.title')}>
            <P>{t('docs.cloud.cloudflare.requirements.description')}</P>
            <Table
              headers={['Field', 'Required', 'Description']}
              rows={[
                ['OTLP Push', 'Workers/Pages only', t('docs.cloud.cloudflare.requirements.otlp')],
                ['API Token', 'API polling', t('docs.cloud.cloudflare.requirements.token')],
                ['Account ID', 'API polling', t('docs.cloud.cloudflare.requirements.accountId')],
                ['Zone ID', 'Optional', t('docs.cloud.cloudflare.requirements.zoneId')],
              ]}
            />
            <Callout type="tip">{t('docs.cloud.cloudflare.requirements.tokenTip')}</Callout>
            <CodeBlock title="Required API Token Permissions">{`• Zone: Analytics: Read
• Zone: Firewall Services: Read
• Zone: Logs: Read (Enterprise only)
• Account: Account Settings: Read (for audit logs)
• Account: Zero Trust: Read (for Access & Gateway logs)
• Account: Cloudflare Pages: Read (for Pages deployments)`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Webhooks */}
        <Section id="webhooks" title={t('docs.cloud.webhooks.title')}>
          <P>{t('docs.cloud.webhooks.description')}</P>
          <CodeBlock title="Webhook URL Format">{`POST https://api.logpulse.io/api/v1/webhooks/cloud/:integrationId`}</CodeBlock>
          <P>{t('docs.cloud.webhooks.setup')}</P>
          <Callout type="info">{t('docs.cloud.webhooks.security')}</Callout>
        </Section>

        {/* Polling & Sync */}
        <Section id="polling" title={t('docs.cloud.polling.title')}>
          <P>{t('docs.cloud.polling.description')}</P>
          <Table
            headers={['Setting', 'Description']}
            rows={[
              [t('docs.cloud.polling.intervalLabel'), t('docs.cloud.polling.intervalDesc')],
              [t('docs.cloud.polling.deduplicationLabel'), t('docs.cloud.polling.deduplicationDesc')],
              [t('docs.cloud.polling.retryLabel'), t('docs.cloud.polling.retryDesc')],
            ]}
          />
          <Callout type="tip">{t('docs.cloud.polling.tip')}</Callout>
        </Section>

        {/* Data Models */}
        <Section id="data-models" title={t('docs.cloud.dataModels.title')}>
          <P>{t('docs.cloud.dataModels.description')}</P>
          <Table
            headers={['Model', 'Used By', 'Key Fields']}
            rows={[
              ['CICDPipelineEvent', 'GitHub Actions, Azure DevOps, Cloudflare Pages', t('docs.cloud.dataModels.cicdFields')],
              ['DeploymentEvent', 'Railway', t('docs.cloud.dataModels.deployFields')],
              ['AuditEvent', 'Azure DevOps', t('docs.cloud.dataModels.auditFields')],
              ['SecurityEvent', 'Cloudflare', t('docs.cloud.dataModels.securityFields')],
              ['ApplicationLog', 'Cloudflare Workers', t('docs.cloud.dataModels.appLogFields')],
              ['TraceEvent', 'Cloudflare Workers', t('docs.cloud.dataModels.traceFields')],
              ['ObservabilityAlert', 'Cloudflare', t('docs.cloud.dataModels.alertFields')],
            ]}
          />
          <P>{t('docs.cloud.dataModels.queryTip')}</P>
          <CodeBlock title="LPQL Example: Query CI/CD events">{`sourcetype="github_actions" status="failure" | stats count by workflow_name`}</CodeBlock>
        </Section>

        {/* Connection Validation */}
        <Section id="validation" title={t('docs.cloud.validation.title')}>
          <P>{t('docs.cloud.validation.description')}</P>
          <P>{t('docs.cloud.validation.checks')}</P>
          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4">
            <li>{t('docs.cloud.validation.check1')}</li>
            <li>{t('docs.cloud.validation.check2')}</li>
            <li>{t('docs.cloud.validation.check3')}</li>
            <li>{t('docs.cloud.validation.check4')}</li>
          </ul>
          <Callout type="warning">{t('docs.cloud.validation.warning')}</Callout>
        </Section>

        {/* Managing Integrations */}
        <Section id="managing" title={t('docs.cloud.managing.title')}>
          <P>{t('docs.cloud.managing.description')}</P>
          <Table
            headers={['Action', 'Description']}
            rows={[
              [t('docs.cloud.managing.pauseLabel'), t('docs.cloud.managing.pauseDesc')],
              [t('docs.cloud.managing.editLabel'), t('docs.cloud.managing.editDesc')],
              [t('docs.cloud.managing.deleteLabel'), t('docs.cloud.managing.deleteDesc')],
              [t('docs.cloud.managing.logsLabel'), t('docs.cloud.managing.logsDesc')],
            ]}
          />
        </Section>

        {/* Coming Soon */}
        <Section id="coming-soon" title={t('docs.cloud.comingSoon.title')}>
          <P>{t('docs.cloud.comingSoon.description')}</P>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-4">
            {['Argo CD', 'Flux CD', 'GitLab CI', 'Tekton', 'Jenkins', 'Amazon EKS', 'Google GKE', 'Azure AKS', 'Falco', 'Trivy', 'HashiCorp Vault', 'Prometheus'].map((name) => (
              <div key={name} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                {name}
              </div>
            ))}
          </div>
          <Callout type="info">{t('docs.cloud.comingSoon.note')}</Callout>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
